'use client';

import { useState } from 'react';

const INITIAL_STATE = {
  birthDate: '1990-01-01',
};

const PRESETS = {
  conservative: {
    birthDate: '1960-01-01',
  },
  moderate: {
    birthDate: '1990-01-01',
  },
  aggressive: {
    birthDate: '2010-01-01',
  },
};

export default function CalculatorAge() {
  const [birthDate, setBirthDate] = useState(INITIAL_STATE.birthDate);
  const [isCalculating, setIsCalculating] = useState(false);

  const handleChange = (value: string) => {
    setBirthDate(value);
    setIsCalculating(true);
    setTimeout(() => setIsCalculating(false), 800);
  };

  const calculateAge = () => {
    const birth = new Date(birthDate);
    const today = new Date();
    
    let years = today.getFullYear() - birth.getFullYear();
    let months = today.getMonth() - birth.getMonth();
    let days = today.getDate() - birth.getDate();
    
    if (days < 0) {
      months--;
      days += new Date(today.getFullYear(), today.getMonth(), 0).getDate();
    }
    
    if (months < 0) {
      years--;
      months += 12;
    }
    
    const totalDays = Math.floor((today.getTime() - birth.getTime()) / (1000 * 60 * 60 * 24));
    const totalMonths = years * 12 + months;
    const totalWeeks = Math.floor(totalDays / 7);
    
    return { years, months, days, totalDays, totalMonths, totalWeeks };
  };

  const age = calculateAge();

  const handleReset = () => {
    setBirthDate(INITIAL_STATE.birthDate);
  };

  const handlePreset = (preset: keyof typeof PRESETS) => {
    setBirthDate(PRESETS[preset].birthDate);
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200">
        {isCalculating && (
          <div className="mb-6 bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center">
            <div className="flex items-center justify-center gap-2">
              <div className="w-5 h-5 border-3 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-emerald-700 font-semibold">Calculating...</span>
            </div>
          </div>
        )}

        {/* Preset and Reset Buttons */}
        <div className="mb-6 space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <button
              onClick={() => handlePreset('conservative')}
              className="px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg font-semibold text-sm transition-all border border-blue-200"
            >
              Conservative
            </button>
            <button
              onClick={() => handlePreset('moderate')}
              className="px-3 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-lg font-semibold text-sm transition-all border border-indigo-200"
            >
              Moderate
            </button>
            <button
              onClick={() => handlePreset('aggressive')}
              className="px-3 py-2 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded-lg font-semibold text-sm transition-all border border-purple-200"
            >
              Aggressive
            </button>
          </div>
          <button
            onClick={handleReset}
            className="w-full px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-semibold text-sm transition-all border border-slate-300"
          >
            Reset to Default
          </button>
        </div>

        <div className="mb-8">
          <label className="block text-sm font-semibold text-slate-700 mb-2">Birth Date</label>
          <input
            type="date"
            value={birthDate}
            onChange={(e) => handleChange(e.target.value)}
            className="w-full px-3 py-3 border-2 border-slate-200 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all font-semibold text-slate-900"
          />
        </div>

        <div className="space-y-4">
          <div className="bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-600 rounded-xl p-6 text-white text-center">
            <div className="text-sm font-semibold uppercase tracking-wider opacity-90 mb-2">Your Age</div>
            <div className="text-5xl font-bold mb-2">{age.years}</div>
            <div className="text-lg opacity-90">Years, {age.months} Months, {age.days} Days</div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="bg-slate-50 rounded-lg p-4 text-center">
              <div className="text-xs text-slate-600 mb-1">Total Months</div>
              <div className="text-xl font-bold text-slate-900">{age.totalMonths.toLocaleString()}</div>
            </div>
            <div className="bg-slate-50 rounded-lg p-4 text-center">
              <div className="text-xs text-slate-600 mb-1">Total Weeks</div>
              <div className="text-xl font-bold text-slate-900">{age.totalWeeks.toLocaleString()}</div>
            </div>
            <div className="bg-slate-50 rounded-lg p-4 text-center">
              <div className="text-xs text-slate-600 mb-1">Total Days</div>
              <div className="text-xl font-bold text-slate-900">{age.totalDays.toLocaleString()}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}